import React, { useEffect } from "react";
import { useSearchParams } from "@/hooks/useSearchParams";
import { racingFilters } from "@/features/racing/models/racingFilters";
import {
  TitleFilter,
  TitleFilterItem,
  TitleFilterItems,
} from "sections/Betting/TitleFilter";
import cx from "classnames";
import { Icon } from "library";
import { useHub } from "@/hooks/graphql/useHubs";
import {
  ToggleGroupItem,
  ToggleGroupRoot,
} from "@/components/ToggleGroup/ToggleGroup";
import useHasPermission from "@/hooks/useHasPermission";
import { useRaceMeetingsCounts } from "@/hooks/firestore/v2/betting/useRaceMeetings";
import { getDateRangeForTabName } from "@/features/racing/utils/dates";
import { useTitleOverride } from "@/features/racing/hooks/useTitleOverride";
import { useLocation } from "@/hooks/useLocation";
import { ReactComponent as Australia } from "sections/Betting/Racing/components/assets/icon-australia.svg";
import { ReactComponent as World } from "sections/Betting/Racing/components/assets/icon-world.svg";

import * as styles from "./RacingTitleFilter.module.scss";
import * as titleFilterStyles from "sections/Betting/TitleFilter.module.scss";

type RacingTitleFilterProps = {
  selectedTab?: string;
};

const RacingTitleFilter = ({ selectedTab }: RacingTitleFilterProps) => {
  const selectedTabWithoutSlash = selectedTab?.replace("/", "");
  const location = useLocation();
  const {
    setSearchParams,
    removeSearchParam,
    searchParams: { titles, regions },
  } = useSearchParams(
    racingFilters,
    `/racing/betting/${selectedTabWithoutSlash}/`,
  );
  const hubData = useHub("racing");
  const [startDate, endDate] = getDateRangeForTabName(
    selectedTabWithoutSlash || "",
  );

  // if title is set from outside, force it
  const forcedTitles = useTitleOverride();
  const selectedTitles = forcedTitles.length > 0 ? forcedTitles : titles;
  const hasTitleFilters = (selectedTitles || []).length > 0;

  const counts = useRaceMeetingsCounts({ regions, startDate, endDate });

  const canSubmitInternationalRacingBet = useHasPermission(
    "submitInternationalRacingBet",
  );

  useEffect(() => {
    if (!canSubmitInternationalRacingBet && regions.includes("world")) {
      setSearchParams({ regions: ["au"] });
    }
  }, [canSubmitInternationalRacingBet, location]);

  return (
    <div className={styles.container}>
      <TitleFilter
        className={styles.items}
        selectionType={"multiple"}
        selected={selectedTitles || []}
        onSelect={(newTitles) =>
          setSearchParams({
            titles: newTitles,
          })
        }
      >
        <TitleFilterItem
          onClick={() => removeSearchParam("titles")}
          className={cx({
            [titleFilterStyles.active]: !hasTitleFilters,
          })}
        >
          <Icon
            type={!hasTitleFilters ? "all" : "close"}
            className={cx(styles.filterIcon, {
              [styles.all]: !hasTitleFilters,
            })}
          />
        </TitleFilterItem>

        <TitleFilterItems
          iconClassName={styles.filterIcon}
          titles={hubData?.titles || []}
          counts={counts}
          hideCounts={
            selectedTab === "next-to-jump" || typeof selectedTab === "undefined"
          }
        />
      </TitleFilter>

      <ToggleGroupRoot
        className={styles.regions}
        type="multiple"
        value={regions ?? []}
        onValueChange={(newRegions) => {
          if (newRegions.length === 0) return; // don't allow to de-select all options
          setSearchParams({
            // NOTE: this type casting is due to toggle component only storing values as strings
            regions: newRegions as unknown as typeof regions,
          });
        }}
      >
        <ToggleGroupItem value="au" className={styles.region}>
          <Australia />
        </ToggleGroupItem>
        {canSubmitInternationalRacingBet && (
          <ToggleGroupItem value="world" className={styles.region}>
            <World />
          </ToggleGroupItem>
        )}
      </ToggleGroupRoot>
    </div>
  );
};

export { RacingTitleFilter };
